import { useCallback, useEffect, useMemo, useState } from 'react';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';

import {
    generateAiSourceArtifactForTask,
    getAiSourceArtifactForTask,
    reviewGenericAiSourceArtifact,
    retireAiSourceArtifact,
    saveAiSourceArtifactPayload,
    type AiSourceArtifact,
    type AiSourceArtifactTaskDetail,
} from '../admin/examTaskReviewDataProvider';

type Props = {
    examTaskId: string;
    examName: string;
    examTaskType: string;
};

function fieldLabel(value: string): string {
    return value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/^\w|\s\w/g, (letter) => letter.toUpperCase());
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function PrimitiveValue({ value }: { value: unknown }) {
    if (value === null || value === undefined || value === '') {
        return <Typography variant="body2" color="text.secondary">Not provided</Typography>;
    }
    if (typeof value === 'boolean') {
        return <Chip size="small" label={value ? 'Yes' : 'No'} color={value ? 'success' : 'default'} />;
    }
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{String(value)}</Typography>;
}

function ArtifactField({ label, value, depth = 0 }: { label: string; value: unknown; depth?: number }) {
    if (Array.isArray(value)) {
        return (
            <Box>
                <Typography variant={depth === 0 ? 'subtitle2' : 'body2'} fontWeight={700} gutterBottom>
                    {fieldLabel(label)}
                </Typography>
                {value.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">None</Typography>
                ) : (
                    <Stack spacing={1}>
                        {value.map((item, index) => isRecord(item) || Array.isArray(item) ? (
                            <Card key={`${label}-${index}`} variant="outlined" sx={{ backgroundColor: 'action.hover' }}>
                                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                                    <ArtifactField label={`${fieldLabel(label)} ${index + 1}`} value={item} depth={depth + 1} />
                                </CardContent>
                            </Card>
                        ) : (
                            <Stack key={`${label}-${index}`} direction="row" spacing={1} alignItems="flex-start">
                                <Typography variant="body2" color="text.secondary">{index + 1}.</Typography>
                                <PrimitiveValue value={item} />
                            </Stack>
                        ))}
                    </Stack>
                )}
            </Box>
        );
    }

    if (isRecord(value)) {
        const entries = Object.entries(value);
        return (
            <Box>
                {label ? <Typography variant={depth === 0 ? 'subtitle2' : 'body2'} fontWeight={700} gutterBottom>{fieldLabel(label)}</Typography> : null}
                {entries.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">None</Typography>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: depth === 0 ? 'repeat(2, minmax(0, 1fr))' : '1fr' }, gap: 1.5 }}>
                        {entries.map(([key, item]) => (
                            <Box key={key} sx={{ minWidth: 0 }}>
                                <ArtifactField label={key} value={item} depth={depth + 1} />
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="body2" fontWeight={700}>{fieldLabel(label)}</Typography>
            <PrimitiveValue value={value} />
        </Box>
    );
}

function replaceArtifact(
    detail: AiSourceArtifactTaskDetail | null,
    artifact: AiSourceArtifact,
): AiSourceArtifactTaskDetail {
    return {
        supported: true,
        artifact,
        mediaResourceUrl: detail?.mediaResourceUrl ?? null,
        promptBody: detail?.promptBody ?? null,
        sourceMatchesCurrent: detail?.sourceMatchesCurrent ?? null,
    };
}

export function AiSourceArtifactPanel({ examTaskId, examName, examTaskType }: Props) {
    const notify = useNotify();
    const [detail, setDetail] = useState<AiSourceArtifactTaskDetail | null>(null);
    const [draftJson, setDraftJson] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(true);
    const [action, setAction] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const parsedPayload = useMemo(() => {
        try {
            const parsed = draftJson.trim() ? JSON.parse(draftJson) : null;
            return isRecord(parsed) ? parsed : null;
        } catch {
            return null;
        }
    }, [draftJson]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const next = await getAiSourceArtifactForTask(examTaskId, examName, examTaskType);
            setDetail(next);
            setDraftJson(next.artifact ? JSON.stringify(next.artifact.payload, null, 2) : '');
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'Failed to load the AI source artifact.');
        } finally {
            setLoading(false);
        }
    }, [examTaskId, examName, examTaskType]);

    useEffect(() => { void load(); }, [load]);

    const generate = async (force: boolean) => {
        setAction(force ? 'regenerate' : 'generate');
        setError(null);
        try {
            const artifact = await generateAiSourceArtifactForTask(examTaskId, examName, examTaskType, force);
            setDetail((current) => ({
                ...replaceArtifact(current, artifact),
                sourceMatchesCurrent: artifact.examTaskType === 'DescribeImage' ? null : true,
            }));
            setDraftJson(JSON.stringify(artifact.payload, null, 2));
            notify(force ? 'Preprocessing regenerated.' : 'Preprocessing generated.', { type: 'success' });
        } catch (generationError) {
            setError(generationError instanceof Error ? generationError.message : 'Artifact generation failed.');
        } finally {
            setAction(null);
        }
    };

    const save = async () => {
        if (!detail?.artifact || !parsedPayload) return;
        setAction('save');
        setError(null);
        try {
            const artifact = await saveAiSourceArtifactPayload(detail.artifact.artifactId, parsedPayload, notes);
            setDetail((current) => replaceArtifact(current, artifact));
            setDraftJson(JSON.stringify(artifact.payload, null, 2));
            setNotes('');
            notify('Artifact edits validated and saved.', { type: 'success' });
        } catch (saveError) {
            setError(saveError instanceof Error ? saveError.message : 'Artifact save failed.');
        } finally {
            setAction(null);
        }
    };

    const retire = async () => {
        if (!detail?.artifact) return;
        setAction('retire');
        setError(null);
        try {
            await retireAiSourceArtifact(detail.artifact.artifactId);
            await load();
            notify('Generated artifact deleted.', { type: 'success' });
        } catch (retireError) {
            setError(retireError instanceof Error ? retireError.message : 'Artifact retirement failed.');
        } finally {
            setAction(null);
        }
    };

    const approve = async () => {
        if (!detail?.artifact || detail.artifact.status !== 'Candidate') return;
        setAction('approve');
        setError(null);
        try {
            const approved = await reviewGenericAiSourceArtifact(
                detail.artifact.artifactId,
                'Approve',
                notes,
            );
            setDetail((current) => replaceArtifact(current, approved));
            setNotes('');
            notify('Preprocessing approved for learner scoring.', { type: 'success' });
        } catch (approvalError) {
            setError(approvalError instanceof Error ? approvalError.message : 'Failed to approve preprocessing.');
        } finally {
            setAction(null);
        }
    };

    if (!loading && detail?.supported === false) return null;

    const artifact = detail?.artifact;
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="h6">AI Scoring Source Artifact</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {fieldLabel(examTaskType)} preprocessing details. Only an approved, current-source artifact is used for learner scoring.
                        </Typography>
                    </Box>
                    {error ? <Alert severity="error">{error}</Alert> : null}
                    {detail?.sourceMatchesCurrent === false ? (
                        <Alert severity="warning">This artifact is stale because its source no longer matches the current task. Regenerate it before scoring.</Alert>
                    ) : null}
                    {artifact?.status === 'Candidate' ? (
                        <Alert severity="info">This is a generated candidate. Review it, then approve it before learner scoring can use it.</Alert>
                    ) : null}
                    {loading ? (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography variant="body2">Loading AI scoring artifact...</Typography>
                        </Stack>
                    ) : !artifact ? (
                        <Stack spacing={1.5} alignItems="flex-start">
                            <Alert severity="info">No preprocessing artifact exists for this task.</Alert>
                            <Button variant="contained" startIcon={<AutoFixHighOutlinedIcon />} disabled={Boolean(action)} onClick={() => void generate(false)}>
                                {action === 'generate' ? 'Generating...' : 'Generate preprocessing'}
                            </Button>
                        </Stack>
                    ) : (
                        <>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} flexWrap="wrap">
                                <Chip
                                    label={`Status: ${artifact.status === 'Retired' ? 'Deleted' : artifact.status}`}
                                    color={artifact.status === 'Approved' ? 'success' : artifact.status === 'Retired' ? 'error' : 'warning'}
                                />
                                <Chip label={artifact.artifactKind} variant="outlined" />
                                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                    {artifact.generatorProvider} / {artifact.generatorModel} · Prompt: {artifact.promptVersion} · Schema: {artifact.schemaVersion}
                                </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                Source SHA-256: {artifact.sourceSha256}
                            </Typography>
                            <Divider />
                            <Box>
                                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Readable preprocessing details</Typography>
                                <ArtifactField label="" value={artifact.payload} />
                            </Box>
                            {isRecord(artifact.qualityFlags) && Object.keys(artifact.qualityFlags).length ? (
                                <>
                                    <Divider />
                                    <ArtifactField label="Quality Flags" value={artifact.qualityFlags} />
                                </>
                            ) : null}
                            <Divider />
                            <TextField
                                label="Artifact JSON"
                                value={draftJson}
                                onChange={(event) => setDraftJson(event.target.value)}
                                multiline
                                minRows={10}
                                fullWidth
                                error={!parsedPayload}
                                helperText={parsedPayload ? 'Server-side task validation runs before saving.' : 'Enter a valid JSON object.'}
                            />
                            <TextField label="Admin review notes" value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={2} fullWidth />
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end" flexWrap="wrap">
                                <Button variant="outlined" disabled={Boolean(action)} onClick={() => void generate(true)}>Regenerate preprocessing</Button>
                                <Button color="warning" disabled={Boolean(action)} onClick={() => void retire()}>Delete generated artifact</Button>
                                {artifact.status === 'Candidate' ? (
                                    <Button variant="contained" disabled={Boolean(action)} onClick={() => void approve()}>
                                        {action === 'approve' ? 'Approving...' : 'Approve preprocessing'}
                                    </Button>
                                ) : null}
                                <Button variant="contained" disabled={Boolean(action) || !parsedPayload} onClick={() => void save()}>
                                    {action === 'save' ? 'Saving...' : 'Save edits and approve'}
                                </Button>
                            </Stack>
                        </>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
